import {
    Organisatie,
    OrganisatieDisplay,
    Rijtijd,
    Routesoort,
    RoutesoortDisplay,
    TvvStandaardRoute,
    Voertuigsoort,
    VoertuigsoortDisplay,
} from "@/entities/tvv";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Label,
    MessageBar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui";
import { useCallback, useState } from "react";
import { EditableStation, EditableStationsList } from "./editable-stations-list";

type RouteBewerkenDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    route: TvvStandaardRoute | null;
    onApply: (updatedRoute: TvvStandaardRoute) => void;
};

function toEditableStations(stationCodes: string[]): EditableStation[] {
    return stationCodes.map((code) => ({ id: crypto.randomUUID(), code }));
}

/** Handmatige routes (nieuwe routes) krijgen een negatief ID: bij bestelling worden ze zonder koppeling aan een standaardroute verstuurd. */
let nextCustomRouteId = -1;
const generateCustomRouteId = () => nextCustomRouteId--;

export function RouteBewerkenDialog({ open, onOpenChange, route, onApply }: Readonly<RouteBewerkenDialogProps>) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {open && <RouteBewerkenDialogContent route={route} onApply={onApply} onClose={() => onOpenChange(false)} />}
        </Dialog>
    );
}

type RouteBewerkenDialogContentProps = {
    route: TvvStandaardRoute | null;
    onApply: (updatedRoute: TvvStandaardRoute) => void;
    onClose: () => void;
};

function RouteBewerkenDialogContent({ route, onApply, onClose }: Readonly<RouteBewerkenDialogContentProps>) {
    const isNewRoute = route === null;

    const [organisatie, setOrganisatie] = useState<Organisatie | undefined>(route?.organisatie);
    const [routeSoort, setRouteSoort] = useState<Routesoort>(route?.routeSoort ?? Routesoort.Stop);
    const [voertuigSoort, setVoertuigSoort] = useState<Voertuigsoort>(route?.voertuigSoort ?? Voertuigsoort.Bus);
    const [stations, setStations] = useState<EditableStation[]>(() =>
        route === null
            ? [
                  { id: crypto.randomUUID(), code: "" },
                  { id: crypto.randomUUID(), code: "" },
              ]
            : toEditableStations(route.stationCodes)
    );
    const [rijtijden, setRijtijden] = useState<Rijtijd[]>(route?.rijtijden ?? []);
    const [hasLoadingRijtijden, setHasLoadingRijtijden] = useState(false);
    const [hasInvalidRijtijden, setHasInvalidRijtijden] = useState(false);

    const handleRijtijdChange = useCallback((beginStation: string, eindStation: string, reisMinuten: number | undefined) => {
        setRijtijden((prev) => {
            if (reisMinuten === undefined) {
                return prev.filter((r) => !(r.beginStation === beginStation && r.eindStation === eindStation));
            }
            const existing = prev.findIndex((r) => r.beginStation === beginStation && r.eindStation === eindStation);
            if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = { beginStation, eindStation, reisMinuten };
                return updated;
            }
            return [...prev, { beginStation, eindStation, reisMinuten }];
        });
    }, []);

    const handleStationsChange = useCallback((newStations: EditableStation[]) => {
        setStations(newStations);
        // Clean up rijtijden that no longer match adjacent station pairs
        setRijtijden((prev) => {
            const validPairs = new Set<string>();
            for (let i = 0; i < newStations.length - 1; i++) {
                validPairs.add(`${newStations[i].code}:${newStations[i + 1].code}`);
            }
            return prev.filter((r) => validPairs.has(`${r.beginStation}:${r.eindStation}`));
        });
    }, []);

    const handleApply = () => {
        if (!organisatie) return;

        const stationCodes = stations.map((s) => s.code).filter((c) => c.length > 0);

        // Build complete rijtijden array covering all adjacent station pairs.
        // Segments without a known rijtijd get reisMinuten: undefined so that
        // getTotaleRijtijd correctly returns undefined when any segment is unknown.
        const completeRijtijden: Rijtijd[] = [];
        for (let i = 0; i < stationCodes.length - 1; i++) {
            const existing = rijtijden.find((r) => r.beginStation === stationCodes[i] && r.eindStation === stationCodes[i + 1]);
            completeRijtijden.push(existing ?? { beginStation: stationCodes[i], eindStation: stationCodes[i + 1] });
        }

        const isOvRoute = TvvStandaardRoute.isOVRoute(voertuigSoort, organisatie);

        if (route !== null) {
            const rijtijdenChanged =
                completeRijtijden.length !== route.rijtijden.length ||
                completeRijtijden.some((r) => {
                    const original = route.rijtijden.find((o) => o.beginStation === r.beginStation && o.eindStation === r.eindStation);
                    return !original || original.reisMinuten !== r.reisMinuten;
                });

            const hasChanges =
                organisatie !== route.organisatie ||
                routeSoort !== route.routeSoort ||
                voertuigSoort !== route.voertuigSoort ||
                stationCodes.length !== route.stationCodes.length ||
                stationCodes.some((code, i) => code !== route.stationCodes[i]) ||
                rijtijdenChanged;

            if (!hasChanges) {
                onClose();
                return;
            }
        }

        const updatedRoute: TvvStandaardRoute = {
            routeId: route?.routeId ?? generateCustomRouteId(),
            stationCodes,
            organisatie,
            voertuigSoort,
            routeSoort,
            opmerking: route?.opmerking,
            voorwaarde: route?.voorwaarde,
            periodeFactoren: route?.periodeFactoren ?? [],
            rijtijden: completeRijtijden,
            totaleRijtijd: Rijtijd.getTotaleRijtijd(completeRijtijden),
            isOvRoute,
        };

        onApply(updatedRoute);
        onClose();
    };

    const filledStations = stations.filter((s) => s.code.length > 0).length;
    const hasEnoughStations = filledStations >= 2;
    const hasEmptyStations = hasEnoughStations && stations.some((s) => s.code.length === 0);
    const filledCodes = stations.map((s) => s.code).filter((c) => c.length > 0);
    const hasConsecutiveDuplicates = filledCodes.some((code, i) => i > 0 && code === filledCodes[i - 1]);

    return (
        <DialogContainer>
            <DialogHeader>
                <DialogTitle>{isNewRoute ? "Nieuwe route" : "Route bewerken"}</DialogTitle>
            </DialogHeader>
            <DialogBody className="space-y-4">
                <p className="text-content-subtle text-s">
                    {isNewRoute
                        ? "Deze route wordt alleen gebruikt voor de huidige bestelling."
                        : "Bewerkingen op een route worden alleen opgeslagen voor de huidige bestelling."}
                </p>

                <div className="flex gap-4">
                    <div className="flex-1">
                        <Label className="mb-1">Vervoerder</Label>
                        <Select value={organisatie ?? ""} onValueChange={(v) => setOrganisatie(v as Organisatie)}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Selecteer vervoerder" />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(OrganisatieDisplay)
                                    .filter(([key]) => !isNewRoute || key === Organisatie.DeWit || key === Organisatie.Transvision)
                                    .map(([key, label]) => (
                                        <SelectItem key={key} value={key}>
                                            {label}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-1">
                        <Label className="mb-1">Routetype</Label>
                        <Select value={routeSoort} onValueChange={(v) => setRouteSoort(v as Routesoort)}>
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(RoutesoortDisplay).map(([key, label]) => (
                                    <SelectItem key={key} value={key}>
                                        {label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-1">
                        <Label className="mb-1">Bustype</Label>
                        <Select value={voertuigSoort} onValueChange={(v) => setVoertuigSoort(v as Voertuigsoort)}>
                            <SelectTrigger className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(VoertuigsoortDisplay)
                                    .filter(([key]) => !isNewRoute || key === Voertuigsoort.Bus || key === Voertuigsoort.Taxibus)
                                    .map(([key, label]) => (
                                        <SelectItem key={key} value={key}>
                                            {label}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <EditableStationsList
                    stations={stations}
                    rijtijden={rijtijden}
                    onStationsChange={handleStationsChange}
                    onRijtijdChange={handleRijtijdChange}
                    onHasLoadingRijtijden={setHasLoadingRijtijden}
                    onHasInvalidRijtijden={setHasInvalidRijtijden}
                />

                {hasEmptyStations && <MessageBar variant="error">Niet alle stations zijn ingevuld.</MessageBar>}
                {hasConsecutiveDuplicates && <MessageBar variant="error">Route bevat opeenvolgende dubbele stations.</MessageBar>}
            </DialogBody>
            <DialogFooter>
                <Button variant="tertiary" onClick={onClose}>
                    Annuleren
                </Button>
                <Button
                    variant="primary"
                    onClick={handleApply}
                    disabled={
                        !organisatie ||
                        !hasEnoughStations ||
                        hasEmptyStations ||
                        hasConsecutiveDuplicates ||
                        hasLoadingRijtijden ||
                        hasInvalidRijtijden
                    }
                >
                    {isNewRoute ? "Toevoegen" : "Toepassen"}
                </Button>
            </DialogFooter>
        </DialogContainer>
    );
}
